import { Fragment, useMemo, useRef, useState } from 'react';

export interface PicklistOption {
  readonly id: string;
  readonly label: string;
  /** Said quietly after the label: why this option is where it is. */
  readonly hint?: string | undefined;
  /** A rule above it: where one group of options gives way to the next. */
  readonly dividerBefore?: boolean | undefined;
}

export interface PicklistEditorProps {
  options: readonly PicklistOption[];
  selected: readonly string[];
  multiple: boolean;
  onToggle: (id: string) => void;
  onCommit: () => void;
  onRevert: () => void;
}

/**
 * The editor behind both picklist cells: search, then space to toggle, Enter to
 * commit, Escape to revert. A single-select commits as soon as a choice is made,
 * because there is nothing else to say.
 */
export function PicklistEditor(props: PicklistEditorProps): React.JSX.Element {
  const [search, setSearch] = useState('');
  const rows = useRef<(HTMLDivElement | null)[]>([]);

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === '') return props.options;
    return props.options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [props.options, search]);

  function moveFocus(from: number, delta: number): void {
    const next = rows.current[from + delta];
    if (next !== null && next !== undefined) next.focus();
  }

  return (
    <div
      className="picklist"
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          props.onCommit();
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          props.onRevert();
        }
      }}
    >
      <input
        className="picklist-search"
        autoFocus
        placeholder="Search…"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            rows.current[0]?.focus();
          }
        }}
      />
      <div className="picklist-options" role="listbox">
        {shown.length === 0 && <p className="subtle picklist-empty">Nothing matches.</p>}
        {shown.map((option, index) => {
          const checked = props.selected.includes(option.id);
          return (
            <Fragment key={option.id}>
              {option.dividerBefore === true && index > 0 && (
                <hr className="picklist-divider" aria-hidden="true" />
              )}
              <div
                ref={(node) => {
                  rows.current[index] = node;
                }}
                role="option"
                aria-selected={checked}
                tabIndex={0}
                className={`picklist-option${checked ? ' checked' : ''}`}
                onClick={() => props.onToggle(option.id)}
                onKeyDown={(event) => {
                  if (event.key === ' ') {
                    event.preventDefault();
                    props.onToggle(option.id);
                  }
                  if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    moveFocus(index, 1);
                  }
                  if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    moveFocus(index, -1);
                  }
                }}
              >
                <span className="picklist-mark">{checked ? '✓' : ''}</span>
                {option.label}
                {option.hint !== undefined && (
                  <span className="picklist-hint subtle">{option.hint}</span>
                )}
              </div>
            </Fragment>
          );
        })}
      </div>
      {props.multiple && (
        <button className="button small" type="button" onClick={props.onCommit}>
          Done (Enter)
        </button>
      )}
    </div>
  );
}
